/**
 * Department Service
 * 
 * 處理部門相關的 API 請求和資料操作
 * 
 * =================================================================
 * 對外提供的方法 (Public Methods)
 * =================================================================
 * 
 * 【部門資料查詢】
 * - getDepartment(departmentId: string): Promise<Department | null>
 *   功用: 獲取指定部門的完整資料
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 部門 ID
 *   輸出: Department 物件或 null
 *   權限: 同組織內的用戶
 * 
 * - getDepartments(): Promise<Department[]>
 *   功用: 獲取同組織內的所有部門列表
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 無（自動使用當前用戶的組織ID）
 *   輸出: Department 陣列
 *   權限: 同組織內的用戶
 * 
 * 【部門管理操作】
 * - createDepartment(departmentData: DepartmentFormData): Promise<Department>
 *   功用: 創建新部門
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 部門基本資料 { name, parentId?, managerId? }
 *   輸出: 創建的 Department 物件
 *   權限: admin, owner
 * 
 * - updateDepartment(departmentId: string, departmentData: Partial<DepartmentFormData>): Promise<Department>
 *   功用: 更新部門資料
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 部門 ID + 更新欄位
 *   輸出: 更新後的 Department 物件
 *   權限: admin, owner
 * 
 * - deleteDepartment(departmentId: string): Promise<boolean>
 *   功用: 刪除部門
 *   操作: 前端操作 (Firestore SDK)
 *   輸入: 部門 ID
 *   輸出: boolean（是否成功刪除）
 *   權限: admin, owner
 * 
 * =================================================================
 * 權限說明:
 * - owner: 擁有全部權限
 * - admin: 可管理部門
 * - member: 僅能查看部門資料
 * =================================================================
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuthStore } from '../stores/auth'

// ================================ 
// Interfaces
// ================================ 

export interface Department {
  id: string
  name: string
  organizationId: string
  parentId?: string | null
  managerId?: string | null
  level: number
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface DepartmentFormData {
  name: string
  parentId?: string | null
  managerId?: string | null
}

// ================================ 
// Private Functions
// ================================ 

/**
 * 重新計算並更新組織內所有部門的層級
 * 私有函式，用於在新增或修改部門後重新計算層級
 */
const updateAllDepartmentsLevel = async (organizationId: string): Promise<void> => {
  try {
    console.log('🔄 開始重新計算部門層級:', organizationId)
    
    // 獲取組織內所有部門
    const departmentsQuery = query(
      collection(db, 'departments'),
      where('organizationId', '==', organizationId)
    )
    const querySnapshot = await getDocs(departmentsQuery)
    
    const departments: (Department & { docRef: any })[] = []
    querySnapshot.forEach((doc) => {
      const departmentData = doc.data()
      departments.push({
        id: doc.id,
        docRef: doc.ref,
        ...departmentData,
        createdAt: departmentData.createdAt?.toDate?.() || departmentData.createdAt,
        updatedAt: departmentData.updatedAt?.toDate?.() || departmentData.updatedAt
      } as Department & { docRef: any })
    })
    
    // 建立部門層級映射
    const departmentLevels = new Map<string, number>()
    
    // 找出所有根部門（parentId 為 null 或空）
    const rootDepartments = departments.filter(dept => !dept.parentId)
    
    // BFS 計算層級
    const queue: { deptId: string; level: number }[] = []
    
    // 根部門設為 level 1
    rootDepartments.forEach(dept => {
      departmentLevels.set(dept.id, 1)
      queue.push({ deptId: dept.id, level: 1 })
    })
    
    // BFS 遍歷計算子部門層級
    while (queue.length > 0) {
      const { deptId, level } = queue.shift()!
      
      // 找出當前部門的所有子部門
      const childDepartments = departments.filter(dept => dept.parentId === deptId)
      
      childDepartments.forEach(childDept => {
        const childLevel = level + 1
        departmentLevels.set(childDept.id, childLevel)
        queue.push({ deptId: childDept.id, level: childLevel })
      })
    }
    
    // 批次更新所有部門的 level
    const batch = writeBatch(db)
    
    departments.forEach(dept => {
      const newLevel = departmentLevels.get(dept.id) || 1 // 預設為 1
      if (dept.level !== newLevel) {
        batch.update(dept.docRef, { 
          level: newLevel, 
          updatedAt: serverTimestamp() 
        })
      }
    })
    
    await batch.commit()
    console.log('✅ 部門層級更新完成')
    
  } catch (error: any) {
    console.error('❌ 更新部門層級失敗:', error)
    throw new Error(`更新部門層級失敗: ${error.message}`)
  }
}

// ================================ 
// Public Functions
// ================================ 

/**
 * 獲取指定部門資料
 * 權限: 同組織內的用戶
 */
export const getDepartment = async (departmentId: string): Promise<Department | null> => {
  try {
    const authStore = useAuthStore()
    const currentUserOrgId = authStore.organizationId
    
    if (!currentUserOrgId) {
      throw new Error('用戶未屬於任何組織')
    }
    
    console.log('🔍 獲取部門資料:', departmentId)
    const departmentDoc = await getDoc(doc(db, 'departments', departmentId))
    
    if (!departmentDoc.exists()) {
      console.warn(`❌ 部門不存在: ${departmentId}`)
      return null
    }
    
    const departmentData = departmentDoc.data()
    
    // 檢查是否為同組織
    if (departmentData.organizationId !== currentUserOrgId) {
      throw new Error('無權限存取此部門')
    }
    
    const department: Department = {
      id: departmentDoc.id,
      ...departmentData,
      createdAt: departmentData.createdAt?.toDate?.() || departmentData.createdAt,
      updatedAt: departmentData.updatedAt?.toDate?.() || departmentData.updatedAt
    } as Department
    
    console.log('✅ 部門資料獲取成功')
    return department
  } catch (error: any) {
    console.error('獲取部門資料失敗:', error)
    throw new Error(`獲取部門資料失敗: ${error.message}`)
  }
}

/**
 * 獲取同組織的部門列表
 * 權限: 同組織內的用戶
 */
export const getDepartments = async (): Promise<Department[]> => {
  try {
    const authStore = useAuthStore()
    const organizationId = authStore.organizationId
    
    if (!organizationId) {
      throw new Error('用戶未屬於任何組織')
    }
    
    console.log('🔍 獲取同組織部門列表:', organizationId)
    const departmentsQuery = query(
      collection(db, 'departments'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'asc')
    )
    
    const querySnapshot = await getDocs(departmentsQuery)
    const departments: Department[] = []
    
    querySnapshot.forEach((doc) => {
      const departmentData = doc.data()
      departments.push({
        id: doc.id,
        ...departmentData,
        createdAt: departmentData.createdAt?.toDate?.() || departmentData.createdAt,
        updatedAt: departmentData.updatedAt?.toDate?.() || departmentData.updatedAt
      } as Department)
    })
    
    console.log(`✅ 獲取到 ${departments.length} 個部門`)
    return departments
  } catch (error: any) {
    console.error('獲取部門列表失敗:', error)
    throw new Error(`獲取部門列表失敗: ${error.message}`)
  }
}

/**
 * 創建新部門
 * 權限: admin, owner
 */
export const createDepartment = async (departmentData: DepartmentFormData): Promise<Department> => {
  try {
    const authStore = useAuthStore()
    const organizationId = authStore.organizationId
    const userRole = authStore.userRole
    
    if (!organizationId) {
      throw new Error('用戶未屬於任何組織')
    }
    
    // 檢查權限
    if (!userRole || !['admin', 'owner'].includes(userRole)) {
      throw new Error('權限不足，僅 admin 和 owner 可創建部門')
    }
    
    console.log('📝 創建新部門:', departmentData)
    
    // 準備部門資料
    const newDepartmentData = {
      name: departmentData.name.trim(),
      organizationId,
      parentId: departmentData.parentId || null,
      managerId: departmentData.managerId || null,
      level: 1, // 預設層級，稍後會重新計算
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    
    console.log('📝 準備寫入的部門資料:', newDepartmentData)
    
    // 創建部門文件
    const departmentRef = await addDoc(collection(db, 'departments'), newDepartmentData)
    
    // 獲取創建的部門資料
    const createdDoc = await getDoc(departmentRef)
    if (!createdDoc.exists()) {
      throw new Error('創建後無法取得部門資料')
    }
    
    const createdData = createdDoc.data()
    const department: Department = {
      id: createdDoc.id,
      ...createdData,
      createdAt: createdData.createdAt?.toDate?.() || createdData.createdAt,
      updatedAt: createdData.updatedAt?.toDate?.() || createdData.updatedAt
    } as Department
    
    console.log('✅ 部門創建成功:', department)
    
    // 重新計算所有部門層級
    await updateAllDepartmentsLevel(organizationId)
    
    // 重新獲取更新後的部門資料
    const updatedDepartment = await getDepartment(department.id)
    return updatedDepartment || department
  } catch (error: any) {
    console.error('創建部門失敗:', error)
    throw new Error(`創建部門失敗: ${error.message}`)
  }
}

/**
 * 更新部門資料
 * 權限: admin, owner
 */
export const updateDepartment = async (
  departmentId: string, 
  departmentData: Partial<DepartmentFormData>
): Promise<Department> => {
  try {
    const authStore = useAuthStore()
    const organizationId = authStore.organizationId
    const userRole = authStore.userRole
    
    if (!organizationId) {
      throw new Error('用戶未屬於任何組織')
    }
    
    // 檢查權限
    if (!userRole || !['admin', 'owner'].includes(userRole)) {
      throw new Error('權限不足，僅 admin 和 owner 可更新部門')
    }
    
    console.log('📝 更新部門資料:', { departmentId, departmentData })
    
    // 首先檢查部門是否存在且屬於同組織
    const existingDepartment = await getDepartment(departmentId)
    if (!existingDepartment) {
      throw new Error('部門不存在或權限不足')
    }
    
    // 準備更新資料
    const updateData: any = {
      updatedAt: serverTimestamp()
    }
    
    if (departmentData.name !== undefined) {
      updateData.name = departmentData.name.trim()
    }
    
    if (departmentData.parentId !== undefined) {
      updateData.parentId = departmentData.parentId || null
    }
    
    if (departmentData.managerId !== undefined) {
      updateData.managerId = departmentData.managerId || null
    }
    
    // 更新部門文件
    const departmentRef = doc(db, 'departments', departmentId)
    await updateDoc(departmentRef, updateData)
    
    // 獲取更新後的部門資料
    const updatedDoc = await getDoc(departmentRef)
    if (!updatedDoc.exists()) {
      throw new Error('更新後無法取得部門資料')
    }
    
    const updatedData = updatedDoc.data()
    const department: Department = {
      id: updatedDoc.id,
      ...updatedData,
      createdAt: updatedData.createdAt?.toDate?.() || updatedData.createdAt,
      updatedAt: updatedData.updatedAt?.toDate?.() || updatedData.updatedAt
    } as Department
    
    console.log('✅ 部門更新成功:', department)
    
    // 重新計算所有部門層級
    await updateAllDepartmentsLevel(organizationId)
    
    // 重新獲取更新後的部門資料
    const updatedDepartment = await getDepartment(departmentId)
    return updatedDepartment || department
  } catch (error: any) {
    console.error('更新部門失敗:', error)
    throw new Error(`更新部門失敗: ${error.message}`)
  }
}

/**
 * 刪除部門
 * 權限: admin, owner
 */
export const deleteDepartment = async (departmentId: string): Promise<boolean> => {
  try {
    const authStore = useAuthStore()
    const organizationId = authStore.organizationId
    const userRole = authStore.userRole
    
    if (!organizationId) {
      throw new Error('用戶未屬於任何組織')
    }
    
    // 檢查權限
    if (!userRole || !['admin', 'owner'].includes(userRole)) {
      throw new Error('權限不足，僅 admin 和 owner 可刪除部門')
    }
    
    console.log('🗑️ 刪除部門:', departmentId)
    
    // 首先檢查部門是否存在且屬於同組織
    const existingDepartment = await getDepartment(departmentId)
    if (!existingDepartment) {
      throw new Error('部門不存在或權限不足')
    }
    
    // 檢查是否有子部門
    const childDepartmentsQuery = query(
      collection(db, 'departments'),
      where('parentId', '==', departmentId)
    )
    const childDepartmentsSnapshot = await getDocs(childDepartmentsQuery)
    
    if (!childDepartmentsSnapshot.empty) {
      throw new Error('無法刪除，該部門下還有子部門')
    }
    
    // 刪除部門文件
    await deleteDoc(doc(db, 'departments', departmentId))
    
    console.log('✅ 部門刪除成功')
    return true
  } catch (error: any) {
    console.error('刪除部門失敗:', error)
    throw new Error(`刪除部門失敗: ${error.message}`)
  }
}